import { gatherAnalyticsData } from '@/util/helpers'
import { withMixpanel, getMixpanelSync } from './mixpanelLoader'

const productFor = currency => (currency === 'OOM' ? 'Play Money' : 'Real Money')
const getAllABTests = () => JSON.parse(localStorage.getItem('abTests')) || {}

const abTestList = () =>
  Object.fromEntries(Object.entries(getAllABTests()).map(([key, value]) => ['ab_test_' + key, value]))

export const getAllCampains = () => JSON.parse(localStorage.getItem('campains') || '[]')

const isBot = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.userAgent.match(/.*((AhrefsSiteAudit)|(AhrefsBot)).*/i)
const isMixpanelDisabled = () => import.meta.env.VITE_MIXPANEL_ENABLED === 'false'
const canTrack = () => !isMixpanelDisabled() && !isBot()

async function signUpPageViewed() {
  if (!canTrack()) return

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('sign_up_page_view', { ...analyticsData })
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for sign_up_page_view event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('sign_up_page_view')
    })
  }
}

async function pageView(props = {}) {
  if (!canTrack()) return

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('page_view', {
        provider: props.provider,
        'home-page-text': props['home-page-text'],
        test_register_feed_alg: props.test_register_feed_alg,
        ...analyticsData,
      })
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for page_view event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('page_view', {
        provider: props.provider,
        'home-page-text': props['home-page-text'],
        test_register_feed_alg: props.test_register_feed_alg,
      })
    })
  }
}

async function register(user, props = {}) {
  if (!canTrack()) return

  registerSuperProperties({ provider: props.provider })

  const people = {
    $name: user.name,
    $email: user.email,
    $avatar: user.picture,
    $user_id: user.id,
    username: user.username,
    is_staff: user.is_staff,
    email_confirmed: user.email_confirmed,
    language: user.language,
    sort_algorithm: user.sort_algorithm,
    date_joined: user.date_joined,
    provider: props.provider,
    last_activity: 'login',
  }

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.identify(user.id)
      mixpanel.track('register', {
        provider: props.provider,
        ...props,
        ...analyticsData,
      })
      mixpanel.people.set(people)
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for register event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.identify(user.id)
      mixpanel.track('register', {
        provider: props.provider,
        ...props,
      })
      mixpanel.people.set(people)
    })
  }
}

async function login(user, props = {}) {
  if (!canTrack()) return

  registerSuperProperties({ provider: props.provider })

  const people = {
    $name: user.name,
    $email: user.email,
    $avatar: user.picture,
    $user_id: user.id,
    username: user.username,
    is_staff: user.is_staff,
    email_confirmed: user.email_confirmed,
    language: user.language,
    sort_algorithm: user.sort_algorithm,
    date_joined: user.date_joined,
    provider: props.provider,
    last_activity: 'login',
  }

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.identify(user.id)
      mixpanel.track('login', {
        provider: props.provider,
        ...props,
        ...analyticsData,
      })
      mixpanel.people.set(people)
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for login event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.identify(user.id)
      mixpanel.track('login', {
        provider: props.provider,
        ...props,
      })
      mixpanel.people.set(people)
    })
  }
}

async function logout() {
  if (!canTrack()) return

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('logout', { ...analyticsData })
      // reset() clears persistence, super properties included, so the
      // device-scoped ones are registered again (provider is user-scoped and
      // correctly dropped).
      mixpanel.reset()
      mixpanel.register({ ...abTestList() })
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for logout event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('logout')
      mixpanel.reset()
      mixpanel.register({ ...abTestList() })
    })
  }
}

async function bet(bet, amountInUsd) {
  if (!canTrack()) return

  const payload = {
    bet_id: bet.id,
    market_id: bet.question.id,
    market_title: bet.question.title,
    outcome_id: bet.outcome.id,
    outcome_title: bet.outcome.title,
    currency: bet.currency,
    shares: bet.shares,
    amount: bet.total_amount,
    amount_in_usd: amountInUsd,
    product: productFor(bet.currency),
    ...abTestList(),
    all_cmpains: getAllCampains(),
  }

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('bet', { ...payload, ...analyticsData })
      mixpanel.people.set({ last_bet: new Date().toISOString(), last_activity: 'bet' })
      mixpanel.people.set_once({ first_bet: new Date().toISOString() })
      mixpanel.people.increment('lifetime_bets')
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for bet event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('bet', payload)
    })
  }
}

async function sell(bet) {
  if (!canTrack()) return

  const payload = {
    'bet-id': bet.id,
    'market-id': bet.question.id,
    'market-title': bet.question.title,
    'outcome-id': bet.outcome.id,
    'outcome-title': bet.outcome.title,
    currency: bet.currency,
    shares: bet.shares,
    amount: bet.total_amount,
    ...abTestList(),
  }

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('sell', { ...payload, ...analyticsData })
      mixpanel.people.set({ last_sell: new Date().toISOString(), last_activity: 'sell' })
      mixpanel.people.set_once({ first_sell: new Date().toISOString() })
      mixpanel.people.increment('lifetime_sells')
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for sell event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('sell', payload)
    })
  }
}

async function deposit() {
  if (!canTrack()) return

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('deposit', { ...abTestList(), ...analyticsData })
      mixpanel.people.set({ last_deposit: new Date().toISOString(), last_activity: 'deposit' })
      mixpanel.people.set_once({ first_deposit: new Date().toISOString() })
      mixpanel.people.increment('lifetime_deposits')
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for deposit event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('deposit', { ...abTestList() })
    })
  }
}

async function initiateDeposit(additionalContext = {}) {
  if (!canTrack()) return

  try {
    const analyticsData = await gatherAnalyticsData(additionalContext)
    await withMixpanel((mixpanel) => {
      mixpanel.track('initiate_deposit', { ...abTestList(), ...analyticsData })
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for initiate_deposit event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('initiate_deposit', { ...abTestList(), ...additionalContext })
    })
  }
}

async function withdraw() {
  if (!canTrack()) return

  try {
    const analyticsData = await gatherAnalyticsData()
    await withMixpanel((mixpanel) => {
      mixpanel.track('withdraw', { ...abTestList(), ...analyticsData })
      mixpanel.people.set({ last_withdrawal: new Date().toISOString(), last_activity: 'withdraw' })
      mixpanel.people.set_once({ first_withdrawal: new Date().toISOString() })
      mixpanel.people.increment('lifetime_withdrawals')
    })
  } catch (error) {
    console.warn('Failed to gather analytics data for withdraw event:', error)
    await withMixpanel((mixpanel) => {
      mixpanel.track('withdraw', { ...abTestList() })
    })
  }
}

function identify(userId) {
  if (!canTrack()) return
  withMixpanel((mixpanel) => {
    mixpanel.identify(userId)
  })
}
function addProperty(key, value) {
  if (!canTrack()) return
  withMixpanel((mixpanel) => {
    mixpanel?.people?.set({ [key]: value })
  })
}

// Lightweight step events for the deposit funnel (deposit_address_created,
// deposit_address_copied, deposit_gate_shown, ...). Fire-and-forget so a slow
// gatherAnalyticsData never delays or drops a step; withMixpanel queues until
// init. These are called from render effects, so the rejection is swallowed
// rather than left to surface as an unhandled rejection.
function trackDepositStep(eventName, props = {}) {
  if (!canTrack()) return
  void withMixpanel((mixpanel) => {
    mixpanel.track(eventName, {
      ...abTestList(),
      ...props,
    })
  }).catch(error => {
    console.warn(`Failed to track ${eventName}:`, error)
  })
}

/**
 * Register props that ride along on every subsequent event (Mixpanel super
 * properties). Called at init and again whenever an A/B test is assigned or a
 * user authenticates, since those land after init.
 * @param {object} [props]
 */
function registerSuperProperties(props = {}) {
  if (!canTrack()) return
  void withMixpanel((mixpanel) => {
    mixpanel.register({
      ...abTestList(),
      ...props,
    })
  }).catch(error => {
    console.warn('Failed to register super properties:', error)
  })
}

/**
 * Single `click` event for the instrumented surfaces. Takes one object so the
 * three named fields can't be swapped or silently dropped; see
 * `@/constants/analyticsClick` for their vocabulary. Remaining keys are the
 * contextual fields (destination, market_id, market_title, category, ...).
 *
 * Fire-and-forget, and deliberately without gatherAnalyticsData — clicks are
 * on hot paths (the feed) and must not await a store import.
 * @param {object} params
 * @param {string} params.component
 * @param {string} params.element
 * @param {string} [params.source]
 */
function trackClick({ component, element, source, ...props } = {}) {
  if (!canTrack()) return
  if (!component || !element) {
    console.warn('trackClick called without component/element:', { component, element })
    return
  }
  void withMixpanel((mixpanel) => {
    mixpanel.track('click', {
      component,
      element,
      source,
      ...props,
    })
  }).catch(error => {
    console.warn('Failed to track click:', error)
  })
}

const actions = {
  signUpPageViewed,
  register,
  login,
  logout,
  bet,
  sell,
  deposit,
  initiateDeposit,
  trackDepositStep,
  trackClick,
  registerSuperProperties,
  withdraw,
  pageView,
  identify,
  addProperty,
  /** @deprecated sync accessor — prefer async withMixpanel */
  getMixpanelSync,
}

export default actions
